import React, { useMemo } from "react";
import { QuestionBlockHeader } from "./components/QuestionBlockHeader";
import styles from "./index.module.scss";
import { Question } from "../Question";
import { LunWenStuTopics } from "service/exam";
import {
    getQuestionTypeName,
    isTrue,
    QuestionType,
    toNumber
} from "service/service";
import QuestionAnswer from "./components/QuestionAnswer";
import { StuTopics } from "service/homework";
import { sum } from "utils/common";

const typeMap = {
    [QuestionType.DanXuan]: "SingleChoice",
    [QuestionType.DuoXuan]: "MultiChoice",
    [QuestionType.PanDuan]: "SingleChoice",
    [QuestionType.TianKong]: "Fill",
    [QuestionType.ZhuGuan]: "Subjective",
    [QuestionType.TingLi]: "Mixed",
    [QuestionType.WanXingTianKong]: "Mixed",
    [QuestionType.YueDuLiJie]: "Mixed"
} as const;

interface QuestionBlockProps {
    data: StuTopics;
}

export function QuestionBlock({ data }: QuestionBlockProps) {
    const answer = useMemo(() => {
        if (
            data.type === QuestionType.DanXuan ||
            data.type === QuestionType.PanDuan
        ) {
            const options = data.answers.filter(item => isTrue(item.isChoose));
            return String(options?.[0]?.answerId) || undefined;
        }

        if (data.type === QuestionType.DuoXuan) {
            const options = data.answers.filter(item => isTrue(item.isChoose));
            return options.map(item => String(item.answerId));
        }

        if (data.type === QuestionType.TianKong) {
            return data.answers.map(item => item.stuAnswer);
        }

        if (data.type === QuestionType.ZhuGuan) {
            return data.answers?.[0]?.stuAnswer;
        }
    }, [data]);

    const referenceAnswer = useMemo(() => {
        if (
            data.type === QuestionType.DanXuan ||
            data.type === QuestionType.PanDuan
        ) {
            const options = data.answers.filter(item => isTrue(item.isRight));
            return String(options?.[0]?.answerId) || undefined;
        }

        if (data.type === QuestionType.DuoXuan) {
            const options = data.answers.filter(item => isTrue(item.isRight));
            return options.map(item => String(item.answerId));
        }

        if (data.type === QuestionType.TianKong) {
            return data.answers.map(item => item.content);
        }

        if (data.type === QuestionType.ZhuGuan) {
            return data.answers?.[0]?.content;
        }
    }, [data]);

    // 本题是否自动判分
    const isAuto = (data: StuTopics) => {
        const type = typeMap[data.type];
        return !["Subjective", "Mixed"].includes(type);
    };

    // 本题是否正确
    const isCorrect = (data: StuTopics) => {
        if (!isAuto(data)) return; // 注意这里不能返回false, 因为主观题不能自动判分, 并不意味着答错
        if (typeMap[data.type] === "Fill") return isCorrectFill(data);
        return data.answers.every(item => item.isChoose === item.isRight);
    };

    // 填空题是否正确
    const isCorrectFill = (data: StuTopics) => {
        const type = typeMap[data.type];
        if (type !== "Fill") return false;

        return data.answers.every(item => {
            if (!item.stuAnswer?.trim() || !item.content?.trim()) return false;
            return (
                item.stuAnswer.replaceAll(/\s/g, "") ===
                item.content.replaceAll(/\s/g, "")
            );
        });
    };

    // 渲染子题
    const renderSubQuestion = (index: number, data: LunWenStuTopics) => (
        <QuestionBlock data={data} />
    );

    // 学生得分
    const getMarkScore = (data: StuTopics): number => {
        const type = typeMap[data.type];

        if (type === "Mixed")
            return sum(...data.topics.map(item => getMarkScore(item)));

        if (!isAuto(data)) return toNumber(data.markScore);
        if (isCorrect(data)) return toNumber(data.score);
        return 0;
    };

    return (
        <div className={styles.root}>
            <QuestionBlockHeader
                type={getQuestionTypeName(data.type)}
                score={getMarkScore(data)}
                totalScore={toNumber(data.score)}
                auto={isAuto(data)}
            />

            <Question
                className={styles.question}
                type={typeMap[data.type]}
                data={{
                    content: data.trunk,
                    requirement: data.contentText,
                    options: data.answers.map(item => ({
                        id: item.answerId.toString(),
                        content: item.content
                    })),
                    answer,
                    questions: data.topics
                }}
                renderSubQuestion={renderSubQuestion}>
                {typeMap[data.type] !== "Mixed" && (
                    <QuestionAnswer
                        reference={referenceAnswer}
                        isCorrect={isCorrect(data)}
                    />
                )}
            </Question>
        </div>
    );
}
